import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import {
  ChevronDown, ChevronRight, FileText, Search, ArrowUpDown, ArrowUp, ArrowDown,
  Building2, Mail, Phone, Briefcase, RefreshCw
} from 'lucide-react';

export default function DepartmentVendors() {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedVendor, setExpandedVendor] = useState(null);
  const [vendorGLs, setVendorGLs] = useState({});
  const [vendorDepartments, setVendorDepartments] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [sortField, setSortField] = useState('VendorName');
  const [sortDirection, setSortDirection] = useState('asc');

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/vendors', {
        params: { page: 1, pageSize: 1000, includeAll: true }
      });
      setVendors(response.data.vendors || response.data || []);
    } catch (error) {
      console.error('Failed to fetch vendors:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVendorGLs = async (vendorId) => {
    try {
      const response = await apiClient.get(`/vendors/${vendorId}/gl-accounts`);
      setVendorGLs(prev => ({ ...prev, [vendorId]: response.data.glAccounts || response.data }));
    } catch (error) {
      console.error('Failed to fetch vendor GL accounts:', error);
      setVendorGLs(prev => ({ ...prev, [vendorId]: [] }));
    }
  };

  const fetchVendorDepartments = async (vendorId) => {
    try {
      const response = await apiClient.get(`/vendors/${vendorId}/departments`);
      setVendorDepartments(prev => ({ ...prev, [vendorId]: response.data.departments || response.data }));
    } catch (error) {
      console.error('Failed to fetch vendor departments:', error);
      setVendorDepartments(prev => ({ ...prev, [vendorId]: [] }));
    }
  };

  const toggleVendorExpansion = async (vendorId) => {
    if (expandedVendor === vendorId) {
      setExpandedVendor(null);
    } else {
      setExpandedVendor(vendorId);
      if (!vendorGLs[vendorId]) fetchVendorGLs(vendorId);
      if (!vendorDepartments[vendorId]) fetchVendorDepartments(vendorId);
    }
  };

  const formatCurrency = (amount, currency = 'GBP') => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(amount || 0);
  };

  // Sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />;
    return sortDirection === 'asc'
      ? <ArrowUp className="w-3 h-3 ml-1 text-blue-600" />
      : <ArrowDown className="w-3 h-3 ml-1 text-blue-600" />;
  };

  // Filter + sort
  const filteredVendors = useMemo(() => {
    let list = vendors;
    if (statusFilter === 'active') list = list.filter(v => v.IsActive);
    else if (statusFilter === 'inactive') list = list.filter(v => !v.IsActive);

    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      list = list.filter(v =>
        (v.VendorName || '').toLowerCase().includes(q) ||
        (v.ContactEmail || '').toLowerCase().includes(q) ||
        (v.TaxId || '').toLowerCase().includes(q)
      );
    }

    return [...list].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (sortField === 'InvoiceCount') {
        aVal = parseInt(aVal) || 0;
        bVal = parseInt(bVal) || 0;
      } else {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [vendors, searchTerm, statusFilter, sortField, sortDirection]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        <span className="ml-3 text-gray-500">Loading vendors...</span>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Briefcase className="w-7 h-7 text-blue-600" />
            My Department Vendors
          </h1>
          <p className="text-sm text-gray-500 mt-1">Vendors assigned to your departments</p>
        </div>
        <button
          onClick={fetchVendors}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search vendor name, email, tax ID..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <div className="text-sm text-gray-500 self-center">
          {filteredVendors.length} vendor{filteredVendors.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 w-10"></th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('VendorName')}>
                  <span className="inline-flex items-center">Vendor <SortIcon field="VendorName" /></span>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('PaymentTerms')}>
                  <span className="inline-flex items-center">Payment Terms <SortIcon field="PaymentTerms" /></span>
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('InvoiceCount')}>
                  <span className="inline-flex items-center">Invoices <SortIcon field="InvoiceCount" /></span>
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredVendors.length > 0 ? (
                filteredVendors.map((vendor) => (
                  <React.Fragment key={vendor.VendorID}>
                    <tr
                      onClick={() => toggleVendorExpansion(vendor.VendorID)}
                      className={`cursor-pointer transition-colors ${expandedVendor === vendor.VendorID ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                    >
                      <td className="px-4 py-3 text-center">
                        {expandedVendor === vendor.VendorID
                          ? <ChevronDown className="w-4 h-4 text-blue-600 mx-auto" />
                          : <ChevronRight className="w-4 h-4 text-gray-400 mx-auto" />}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">{vendor.VendorName}</div>
                        {vendor.TaxId && <div className="text-xs text-gray-500 mt-0.5">Tax ID: {vendor.TaxId}</div>}
                      </td>
                      <td className="px-4 py-3">
                        {vendor.ContactEmail && (
                          <div className="flex items-center gap-1.5 text-sm text-gray-600">
                            <Mail className="w-3.5 h-3.5 text-gray-400" />
                            {vendor.ContactEmail}
                          </div>
                        )}
                        {vendor.ContactPhone && (
                          <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-0.5">
                            <Phone className="w-3 h-3 text-gray-400" />
                            {vendor.ContactPhone}
                          </div>
                        )}
                        {!vendor.ContactEmail && !vendor.ContactPhone && <span className="text-sm text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {vendor.PaymentTerms || '—'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center gap-1 text-sm font-medium text-gray-700">
                          <FileText className="w-3.5 h-3.5 text-gray-400" />
                          {vendor.InvoiceCount || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex px-2.5 py-0.5 text-xs font-semibold rounded-full ${
                          vendor.IsActive
                            ? 'bg-green-100 text-green-800 border border-green-200'
                            : 'bg-gray-100 text-gray-600 border border-gray-200'
                        }`}>
                          {vendor.IsActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                    </tr>
                    {expandedVendor === vendor.VendorID && (
                      <tr>
                        <td colSpan="6" className="px-4 py-4 bg-gray-50/50 border-b">
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {/* GL Accounts */}
                            <div className="bg-white rounded-lg border border-gray-200 p-4">
                              <h4 className="font-semibold text-sm text-gray-900 mb-3 flex items-center gap-2">
                                <Building2 className="w-4 h-4 text-blue-600" />
                                GL Accounts
                              </h4>
                              {vendorGLs[vendor.VendorID] ? (
                                vendorGLs[vendor.VendorID].length > 0 ? (
                                  <div className="space-y-2">
                                    {vendorGLs[vendor.VendorID].map(gl => (
                                      <div key={gl.GLAccountID} className="flex items-center justify-between py-1.5 px-2 rounded bg-gray-50">
                                        <div>
                                          <span className="text-sm font-mono font-medium text-gray-900">{gl.AccountCode}</span>
                                          <span className="text-sm text-gray-600 ml-2">{gl.AccountName}</span>
                                        </div>
                                        <div className="text-sm font-semibold text-gray-900">
                                          {formatCurrency(gl.TotalAmount)}
                                          <span className="text-xs text-gray-500 ml-1">({gl.LineCount} lines)</span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : <p className="text-sm text-gray-400 text-center py-3">No GL accounts</p>
                              ) : <p className="text-sm text-gray-400 text-center py-3">Loading...</p>}
                            </div>

                            {/* Departments */}
                            <div className="bg-white rounded-lg border border-gray-200 p-4">
                              <h4 className="font-semibold text-sm text-gray-900 mb-3 flex items-center gap-2">
                                <Building2 className="w-4 h-4 text-purple-600" />
                                Assigned Departments
                              </h4>
                              {vendorDepartments[vendor.VendorID] ? (
                                vendorDepartments[vendor.VendorID].length > 0 ? (
                                  <div className="flex flex-wrap gap-2">
                                    {vendorDepartments[vendor.VendorID].map((dept) => (
                                      <span
                                        key={dept.DepartmentID}
                                        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200"
                                      >
                                        {dept.DepartmentName}
                                      </span>
                                    ))}
                                  </div>
                                ) : <p className="text-sm text-gray-400 text-center py-3">Not assigned to departments</p>
                              ) : <p className="text-sm text-gray-400 text-center py-3">Loading...</p>}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-4 py-12 text-center">
                    <Briefcase className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500 font-medium">
                      {searchTerm ? 'No vendors match your search' : 'No vendors assigned'}
                    </p>
                    <p className="text-gray-400 text-sm mt-1">
                      {searchTerm ? 'Try adjusting your search terms' : 'Contact your admin to assign vendors'}
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
